/**
 * Created by thomasd on 2017-02-14.
 */
import { geti18n, get } from '../../utils';
import { calculateLoanRate, calculateFinanceMonthlyPayment, calculateLeaseDueAtSigning, calculateEstimatedAmountFinance, calculateLeaseIncentiveMonthlyPayment, calculateVehicleDeprecation, calculateResidualValue, calculateMonthlyRent, calculateAdjustedCapitalCost, calculateTotalGrossCapitalizedAmount, calculateCapitalCostReduction } from '../../business-logic/calculations';

/**
 * Returns an Array of Mileage Options based on the options available for the
 * trim incentive.
 * @param state
 */
export const getMileageOptions = (state, option) => {
  let options;

  if (option.terms) {
    const availableMileages = Object.keys(option.terms);
    const mileageLabels = geti18n('offerPanel.topPanel.mileageLabels');
    options = availableMileages.map(item => ({ text: mileageLabels[item], value: item }));
  } else {
    options = [];
  }

  return options;
};

/**
 * Returns an Array of Cash Back Options.
 * @param state
 */
// export const getCashBackOptions = (state, option) => {
//   let options = [];
//   const termsCashBack = [];
//   const term = option.term;
//   const cashBackOffers = state.bestOffers.offers.cashBackOffers;
//   const cashBackOffersLength = state.bestOffers.offers.cashBackOfferCountByTerm[`${term}`];
//   if (cashBackOffers && term && cashBackOffersLength && option.paymentType === 'FINANCE') {
//     /* eslint-disable */
//     for (const key in cashBackOffers) {
//       if (cashBackOffers.hasOwnProperty(key)) {
//         if (key.includes(`${term}/`)) {
//           termsCashBack.push(cashBackOffers[key]);
//         }
//       }
//     }
//     /* eslint-enable */
//     options = termsCashBack.map((offer) => {
//       const offerKey = `${offer.term}/${offer.theOfferData.promoId}`;
//       const offerText = formatCurrency(offer.cashBack);
//       return {
//         text: offerText,
//         value: offerKey
//       }
//     });
//   }
//   return options.reverse();
// };

/**
 * Maps the Mileage From an offer, an Integer of 10000, 12000 or 15000 to
 * The proper letter code, X, L or S respectively
 * @param mileage - 10000, 12000 or 15000
 */
export const getSelectedMileage = (mileage) => {
  const mileageOptions = geti18n('offerPanel.topPanel.mileageOptions');
  return mileageOptions[mileage];
}

/**
 * Calculating all Lease Offer variables
 */

/**
 * Popular equipped price is the price used on the current (previous) Payment
 * estimator to determine the starting msrp for all calculations.
 * @param option
 */
export const getPopularEquippedPrice = option => parseInt(get(option, 'popularEquippedPrice'), 10); //eslint-disable-line

export const getTrimPopularEquippedPrice = (state) => {
  const trim = get(state, 'navigation.nCode');
  if (trim) {
    const modelLineCode = get(state, 'navigation.modelLineCode');
    const modelTrims = get(state, `trims.trims.${modelLineCode}`);
    for (let x = 0, y = modelTrims.length; x < y; x++) { //eslint-disable-line
      if (modelTrims[x].nCode === trim) {
        return parseInt(modelTrims[x].popularEquippedPrice, 10);
      }
    }
  }
  return false
};


/**
 * Retrieve Acquisition Fee from Lease Incentive
 * @param option - Lease Incentive
 * @param mileage - The Selected Mileage
 * @param term - The Selected Term
 */
export const getLeaseAcquisitionFeeFromIncentive = (option, mileage, term) => parseInt(get(option, `terms.${mileage}.${term}.acquisitionFee`), 10);

/**
 * Lease Offer calculates Adjusted Capitalized Cost based on offer data not incentive Data
 * @param term
 * @param option
 * @returns {number}
 */
export const getLeaseOfferAdjustedCapitalizedCost = (option, mileage, term) => {
  const popEquippedPrice = getPopularEquippedPrice(option); //eslint-disable-line
  const acquisitionFee = getLeaseAcquisitionFeeFromIncentive(option, mileage, term);
  const downPaymentAmount = parseInt(option.customerDown, 10) || 0;
  const capitalizedCostReduction = calculateCapitalCostReduction(downPaymentAmount, 0, 0);
  const totalGrossCapitalizedAmount = calculateTotalGrossCapitalizedAmount(
    popEquippedPrice,
    acquisitionFee
  );
  const val = calculateAdjustedCapitalCost(
      totalGrossCapitalizedAmount,
      capitalizedCostReduction);
  return val
};

export const getCapitalizedCostReduction = (state) => {
  const downPaymentAmount = get(state, 'userAmounts.downPayment');
  const tradeInValue = get(state, 'userAmounts.tradeIn');
  const retailerAccessoryCost = get(state, 'userAmounts.accessories');
  return calculateCapitalCostReduction(downPaymentAmount, tradeInValue, retailerAccessoryCost)
};

// Need to Alter this one to use option estimated amount, will still work though
export const getLeaseEstimatedAmountFinance = (state, option) => {
  const popularlyEquippedPrice = getPopularEquippedPrice(option);
  const capitalCostReduction = getCapitalizedCostReduction(state);
  return calculateEstimatedAmountFinance(popularlyEquippedPrice, capitalCostReduction);//eslint-disable-line
};

export const getLeaseIncentiveAdjustedCapitalCost = (state, option, mileage, term) => {
  const popularEquippedPrice = getPopularEquippedPrice(option);
  const acquisitionFee = getLeaseAcquisitionFeeFromIncentive(option, mileage, term);
  const capitalizedCostReduction = getCapitalizedCostReduction(state);
  const totalGrossCapitalizedAmount = calculateTotalGrossCapitalizedAmount(popularEquippedPrice, acquisitionFee); //eslint-disable-line
  return calculateAdjustedCapitalCost(totalGrossCapitalizedAmount, capitalizedCostReduction);
};

export const getResidualPCTLeaseIncentive = (option, mileage, term) => parseFloat(get(option, `terms.${mileage}.${term}.residualPct`));

const getMoneyFactor = (option, mileage, term) => parseFloat(get(option, `terms.${mileage}.${term}.moneyFactor`));

/**
 * USES businessLogic/calculations.js!
 * Monthly Payments are the result of a number of different calculations listed above here.
 * These will calculate from different areas based on whether an option is passed through
 * or if the default incentive should be used.
 * @param state
 * @param term
 * @param mileage
 * @param option
 * @returns {number}
 */
export const getLeaseMonthlyPayment = (state, option, mileage, term) => {
  const popularEquippedPrice = getPopularEquippedPrice(option);
  const residualPCT = getResidualPCTLeaseIncentive(option, mileage, term);
  const residualValue = calculateResidualValue(popularEquippedPrice, residualPCT);
  const adjustedCapitalizedCost = getLeaseIncentiveAdjustedCapitalCost(state, option, mileage, term); //eslint-disable-line
  const moneyFactor = getMoneyFactor(option, mileage, term);
  const brand = get(state, 'environment.BRAND_CHANNEL');
  const leaseMonths = parseInt(term, 10);
  const monthlyRent = calculateMonthlyRent(adjustedCapitalizedCost, residualValue, moneyFactor, brand); //eslint-disable-line
  const vehicleDeprication = calculateVehicleDeprecation(adjustedCapitalizedCost, residualValue, leaseMonths); //eslint-disable-line
  return calculateLeaseIncentiveMonthlyPayment(vehicleDeprication, monthlyRent);
};

export const getLeaseIncentiveDueAtSigning = (state, option, mileage, term) => {
  const monthlyPayment = getLeaseMonthlyPayment(state, option, mileage, term);
  const downPaymentAmount = get(state, 'userAmounts.downPayment');
  return calculateLeaseDueAtSigning(downPaymentAmount, monthlyPayment)
};

/**
 * Finance Payment Options
 */
export const getAPR = (option, term, applied) => {
  const termEntry = get(option, ['terms', term]);
  const aprType = (option.isOffer && applied === true && term.applicableToOffer === true) ? 'offerApr' : 'apr';
  const termApr = get(termEntry, [aprType]);
  const aprVal = parseFloat(termApr, 10);
  return aprVal;
}

export const getCashBackOnlyAPR = (option, term) => parseFloat(get(option, `terms.${term}.apr`), 10);

export const getCashbackAmount = option => parseInt(option.amount, 10);

export const getFinanceIncentiveMonthlyPayment = (state, option, term, applied = false) => {
  const popularlyEquippedPrice = getPopularEquippedPrice(option);
  const capitalCostReduction = getCapitalizedCostReduction(state);
  const annualPercentageRate = parseFloat(getAPR(option, term, applied));
  const cashBackAmount = 0;
  const loanRate = calculateLoanRate(annualPercentageRate);
  const estimatedAmountFinanced = calculateEstimatedAmountFinance(popularlyEquippedPrice, capitalCostReduction, cashBackAmount); //eslint-disable-line
  const financedMonths = term;
  const val = calculateFinanceMonthlyPayment(loanRate, estimatedAmountFinanced, financedMonths);
  return val
};

export const getFinanceOfferMonthlyPayment = (state, option, term) => {
  const isCashBackOnly = option.ribbon.offerType === 1;
  const popularlyEquippedPrice = getPopularEquippedPrice(option);
  const capitalCostReduction = getCapitalizedCostReduction(state);
  const annualPercentageRate = isCashBackOnly ? getCashBackOnlyAPR(option, term) : getAPR(option, term); //eslint-disable-line
  const cashBackAmount = (option.ribbon.offerType === 1 || option.ribbon.offerType === 2) ? getCashbackAmount(option) : 0; //eslint-disable-line
  const loanRate = calculateLoanRate(annualPercentageRate);
  const estimatedAmountFinanced = calculateEstimatedAmountFinance(popularlyEquippedPrice, capitalCostReduction, cashBackAmount); //eslint-disable-line
  const financedMonths = term;
  return calculateFinanceMonthlyPayment(loanRate, estimatedAmountFinanced, financedMonths);
};



/** WEBPACK FOOTER **
 ** ./src/redux/selectors/paymentOptionCalculations.js
 **/